import React, { useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface BuffetPeopleModalProps {
  /** Display name of the buffet package */
  packageName: string;
  mealPeriod: 'breakfast' | 'lunch' | 'dinner';
  price: number;
  onConfirm: (numberOfPeople: number) => void | Promise<void>;
  onCancel: () => void;
  /** Disable confirm while the POS is creating the order / opening payment */
  confirming?: boolean;
  /** Inline error from the last place attempt */
  errorMessage?: string | null;
  /** Prefill from reservation party size */
  initialPeople?: number;
  /** When set, explain staff can bump if more guests arrived */
  bookedPartySize?: number;
}

const BuffetPeopleModal: React.FC<BuffetPeopleModalProps> = ({
  packageName,
  mealPeriod,
  price,
  onConfirm,
  onCancel,
  confirming = false,
  errorMessage = null,
  initialPeople,
  bookedPartySize,
}) => {
  const start = Math.max(1, Math.min(50, Math.floor(initialPeople || 1) || 1));
  const [numberOfPeople, setNumberOfPeople] = useState<number>(start);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const peopleKeyboard = useSoftKeyboardField({
    id: 'buffet-people-count',
    type: 'numeric',
    label: 'Number of people',
    value: String(numberOfPeople),
    onChange: (v) => {
      const n = parseInt(v, 10) || 1;
      setNumberOfPeople(Math.max(1, Math.min(50, n)));
      setError('');
    },
    manualValidator: (v) => v === '' || /^\d{1,2}$/.test(v),
  });

  const busy = confirming || submitting;
  const isFromBooking = typeof bookedPartySize === 'number' && bookedPartySize > 0;

  const handleConfirm = async () => {
    if (busy) return;
    if (numberOfPeople < 1) {
      setError('Number of people must be at least 1');
      return;
    }
    if (numberOfPeople > 50) {
      setError('Number of people cannot exceed 50');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(numberOfPeople);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPrice = price * numberOfPeople;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto my-auto mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800 truncate pr-2">
            {packageName}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-800 text-2xl transition-colors"
          >
            ×
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              {mealPeriod.charAt(0).toUpperCase() + mealPeriod.slice(1)} · Price per person:{' '}
              <span className="font-semibold text-gray-800">${price.toFixed(2)}</span>
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {isFromBooking ? 'Actual party size' : 'Number of People'}
            </label>
            {isFromBooking && (
              <p className="text-xs text-gray-600 mb-2">
                Booked for <span className="font-semibold">{bookedPartySize}</span>. Change only if more
                guests arrived (price updates below).
              </p>
            )}
            <input
              type="number"
              min="1"
              max="50"
              value={numberOfPeople}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:outline-none text-lg font-semibold"
              data-soft-keyboard-handled
              {...peopleKeyboard}
              autoFocus={!isFromBooking}
            />
            {error && (
              <p className="text-red-500 text-sm mt-1">{error}</p>
            )}
            {errorMessage && (
              <p className="text-red-600 text-sm mt-2 font-semibold" role="alert">
                {errorMessage}
              </p>
            )}
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-semibold">Total:</span>
              <span className="text-2xl font-bold text-pink-600">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={busy || !(price > 0)}
            className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {busy ? 'Creating order…' : 'Proceed to Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuffetPeopleModal;

